import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, X, Settings, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Picker for Assigned_Crew_Leader.
 * Renders a <select> populated from /api/crew-leaders.
 * Below it: small "+ Add new crew leader" inline form, and a manage link.
 * Backwards-compat: if `value` prop doesn't match any known leader, an extra
 * "(custom) <value>" option is shown so we don't drop pre-existing free-text data.
 */
export default function CrewLeaderPicker({ value, onChange, testid = "crew-leader-picker" }) {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crew-leaders");
      setLeaders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const knownNames = leaders.map((l) => l.name);
  const valueIsKnown = !value || knownNames.includes(value);

  const onAdd = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newName.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/crew-leaders", {
        name: newName.trim(),
        email: newEmail.trim(),
      });
      setLeaders((curr) => [...curr, data].sort((a, b) => a.name.localeCompare(b.name)));
      onChange(data.name);
      setNewName("");
      setNewEmail("");
      setShowAdd(false);
      toast.success(`Added ${data.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex gap-2">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-select`}
          className="rg-tap flex-1 border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none appearance-none"
        >
          <option value="">— Unassigned —</option>
          {!valueIsKnown && (
            <option value={value} data-testid={`${testid}-custom`}>
              (custom) {value}
            </option>
          )}
          {leaders.map((l) => (
            <option key={l.id} value={l.name}>
              {l.name}
              {l.email ? ` · ${l.email}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowAdd((s) => !s)}
          data-testid={`${testid}-add-btn`}
          className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 bg-white rounded-sm flex items-center gap-1 font-bold uppercase tracking-wide text-sm text-slate-700"
          title="Add new crew leader"
        >
          {showAdd ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showAdd ? "Cancel" : "New"}
        </button>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid={`${testid}-refresh`}
          className="rg-tap px-2 border-2 border-slate-300 hover:border-slate-900 bg-white rounded-sm flex items-center text-slate-700"
          title="Refresh list"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {showAdd && (
        <div
          data-testid={`${testid}-add-form`}
          className="border-2 border-slate-300 rounded-sm p-3 bg-slate-50 space-y-2"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAdd(e);
                }
              }}
              data-testid={`${testid}-new-name`}
              placeholder="Name (e.g. R. Mendez)"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
            />
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAdd(e);
                }
              }}
              data-testid={`${testid}-new-email`}
              placeholder="Email (optional)"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
            />
          </div>
          <button
            type="button"
            onClick={onAdd}
            disabled={submitting}
            data-testid={`${testid}-new-submit`}
            className="rg-tap w-full bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Plus strokeWidth={3} className="h-4 w-4" />
            {submitting ? "Adding…" : "Add crew leader"}
          </button>
        </div>
      )}

      <Link
        to="/crew-leaders"
        data-testid={`${testid}-manage-link`}
        className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 font-bold uppercase tracking-wider"
      >
        <Settings className="h-3 w-3" />
        Manage crew leaders
      </Link>
    </div>
  );
}
